import React, { useState, useRef, useEffect } from "react";
import { config } from '../constants.js';
import { getGuestModeActive } from './guestMode.js';
import topicsPathwayArray from './topicsPathwayArray.js';
var url = config.url.API_URL;


// startTime, liftedState(questionsAttempted, questionsCorrect, questionsIncorrect, questionsStrek)
// username
// progress.summerPrep.quadratics.skillData{skill=topic,sessionsData=sessionObj }
// we have to get the endTime in the parent
// const endTime = new Date()
export function setSessionData(liftedState, startTime, totalTime, domain, unit, topic, username) {
    console.log("Total time in import: " + totalTime);
    let sessionObj = {
      "metStandard": true,
      "questionsAttempted": liftedState.questionsAttempted,
      "questionsCorrect": liftedState.questionsCorrect,
      "questionsIncorrect": liftedState.questionsIncorrect,
      "questionsStreak": liftedState.questionsStreak,
      "datetimeStarted": startTime,
      "totalTime": totalTime,
    }
    let sessionData = {
      userData: {
          username: username,
          questionsAttempted: liftedState.questionsAttempted,
          questionsCorrect: liftedState.questionsCorrect,
      },
      progress: {
        [domain]: {
            [unit]: {
                skillData: {
                  skill: topic,
                  sessionsData: sessionObj
                }
            }
        }        
      },
      progressDomain: [domain],
      progressUnit: [unit],
      skillData: {skill: topic, sessionData: sessionObj}
    }
    return sessionData;
}

export async function recordProgress(sessionData, domain) {
    if (getGuestModeActive()) {
        return { skipped: true, guest: true };
    }
    try {
        const response = await fetch(`${url}/record/metStandard/${domain}`, {
            method: "POST",
            credentials: 'include',
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(sessionData),
        });

        if (!response.ok) {
            throw new Error('HTTP error! status: ${response.status}');
        }

        const result = await response.json()
        return result;
    } catch (error) {
        console.error('Error recording progress:', error);
        throw error;
    }
}    

export function setAction(actionType, actionDetails, userId) {
    const action = {
        userId: userId,
        actionType: actionType,
        // Remove explicit timeStamp - let backend handle it consistently
        details: actionDetails,
    }
    return action;
}

// Generate unique session ID
export function generateSessionId() {
    // Use timestamp + random for uniqueness
    return `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

export function setReviewStartedAction(userId, sessionDetails) {
    const actionDetails = {
        sessionId: sessionDetails.sessionId,
        sessionName: sessionDetails.sessionName,
        selectedAllCards: sessionDetails.selectedAllCards,
        selectedTopics: sessionDetails.selectedTopics,
        ratingMode: sessionDetails.ratingMode,
        ratingThreshold: sessionDetails.ratingThreshold,
        cardSetting: sessionDetails.cardSetting,
        selectedCardIds: sessionDetails.selectedCardIds,
        totalCardsAvailable: sessionDetails.totalCardsAvailable,
        matchingCardsCount: sessionDetails.matchingCardsCount,
        sessionTimestamp: sessionDetails.sessionTimestamp,
        initialRatings: sessionDetails.initialRatings,
    };
    return setAction("reviewStarted", actionDetails, userId);
}

export function setReviewCompletedAction(userId, completionDetails) {
    const actionDetails = {
        sessionId: completionDetails.sessionId,
        cardsViewed: completionDetails.cardsViewed,
        ratingsChanged: completionDetails.ratingsChanged,
        initialAverageRating: completionDetails.initialAverageRating,
        initialUnratedCount: completionDetails.initialUnratedCount,
        finalAverageRating: completionDetails.finalAverageRating,
        finalUnratedCount: completionDetails.finalUnratedCount,
        averageRatingChange: completionDetails.averageRatingChange,
        totalTimeSpent: completionDetails.totalTimeSpent,
        completionTimestamp: completionDetails.completionTimestamp,
    };
    return setAction("reviewCompleted", actionDetails, userId);
}

export function setReviewCardRatingAction(userId, cardId, rating, trigger, sessionId = null) {
    const actionDetails = {
        cardId: cardId,
        rating: rating,
        trigger: trigger,
    };
    
    // Include sessionId if provided
    if (sessionId) {
        actionDetails.sessionId = sessionId;
    }
    
    return setAction("reviewCardRating", actionDetails, userId);
}

export async function recordReviewStartedAction(userId, sessionDetails) {
    const action = setReviewStartedAction(userId, sessionDetails);
    return recordAction(action);
}

export async function recordReviewCompletedAction(userId, completionDetails) {
    const action = setReviewCompletedAction(userId, completionDetails);
    return recordAction(action);
}

export async function recordReviewCardRatingAction(userId, cardId, rating, trigger, sessionId = null) {
    const action = setReviewCardRatingAction(userId, cardId, rating, trigger, sessionId);
    return recordAction(action);
}

export async function fetchReviewCardRatingActions(userId) {
    if (!userId) return [];

    const response = await fetch(`${url}/users/getReviewCardRatings/${userId}`, {
        method: "GET",
        credentials: 'include',
        headers: {
            "Content-Type": "application/json",
        },
    });

    if (!response.ok) {
        throw new Error(`Unable to load review card ratings: ${response.status}`);
    }

    const result = await response.json();
    return result.actions || [];
}

// Standalone function to track skill completion for smart progression
async function trackSkillCompletion(userId, skillId, action = 'completed') {
    if (!userId || !skillId) return;

    try {
        // Find the skill data from topicsPathwayArray
        const skillData = topicsPathwayArray.find(skill => String(skill.skillId) === String(skillId));
        if (!skillData) return;

        const lastSkillData = {
            lastSkillId: parseInt(skillId),
            lastSkillTimestamp: new Date().toISOString(),
            lastSkillUnit: skillData.unit,
            lastSkillTopic: skillData.topic,
            lastAction: action
        };

        // Store in localStorage for immediate access
        localStorage.setItem('lastSkill', JSON.stringify(lastSkillData));

        // Also store in database via preferences endpoint
        await fetch(`${url}/users/preferences`, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(lastSkillData)
        });

        console.log(`🎯 Last skill tracked (${action}):`, skillData.skillDisplayName);
    } catch (error) {
        console.error('Error tracking last skill:', error);
    }
}

export async function recordAction(action) {
    if (getGuestModeActive()) {
        return { skipped: true, guest: true };
    }
    try {
        const response = await fetch(`${url}/record/action`, {
            method: "POST",
            mode: 'cors',
            credentials: 'include',
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(action),
        });
    
        if (!response.ok) {
            switch (response.status) {
                case 400:
                    throw new Error("Invalid data sent to the server. Please check and try again.");
                case 401:
                    throw new Error("You are not authorized to perform this action. Please log in again.");
                case 500:
                    throw new Error("A server error occurred. Please try again later.");
                default:
                    throw new Error(`Unexpected error: ${response.status} ${response.statusText}`);
            }
        }
        const result = await response.json();
        console.log("Action recorded successfully:", result);

        // Track skill completion for smart progression
        if (action.actionType === 'skillCompleted' && action.details?.skillId) {
            try {
                await trackSkillCompletion(action.userId, action.details.skillId, 'completed');
                console.log('✅ Skill completion tracked for smart progression');
            } catch (trackingError) {
                console.error('Error tracking skill completion:', trackingError);
                // Don't fail the main action if tracking fails
            }
        }

        return result;
    } catch(error){
        console.error('Error recording action:', error);
        throw error;
    }
}

// ─── Skill question telemetry ─────────────────────────────────────────────────

/**
 * Record a single question attempt (non-blocking; logs warnings on failure).
 */
export function recordSkillQuestionAttempt(payload) {
  if (getGuestModeActive()) {
    return Promise.resolve({ skipped: true, guest: true });
  }
  return fetch(`${url}/record/skillQuestionAttempt`, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
    .then((response) => {
      if (!response.ok) {
        return response.json().then((body) => {
          throw new Error(body.msg || `skillQuestionAttempt failed: ${response.status}`);
        });
      }
      return response.json();
    })
    .catch((error) => {
      console.warn('skillQuestionAttempt failed (non-blocking):', error.message);
      return { success: false };
    });
}

/**
 * Submit student feedback for a question, skill, or pathway (non-blocking).
 */
export function submitStudentFeedback(payload) {
  if (getGuestModeActive()) {
    return Promise.resolve({ skipped: true, guest: true });
  }
  return fetch(`${url}/record/studentFeedback`, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
    .then((response) => {
      if (!response.ok) {
        return response.json().then((body) => {
          throw new Error(body.msg || `studentFeedback failed: ${response.status}`);
        });
      }
      return response.json();
    })
    .catch((error) => {
      console.warn('studentFeedback failed (non-blocking):', error.message);
      return { success: false };
    });
}

/**
 * Record a skill session lifecycle or tab visibility event (non-blocking).
 */
export async function fetchSkillQuestionAttempts(sessionId) {
  const response = await fetch(`${url}/record/skillQuestionAttempts/${encodeURIComponent(sessionId)}`, {
    method: 'GET',
    mode: 'cors',
    credentials: 'include',
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.msg || `fetchSkillQuestionAttempts failed: ${response.status}`);
  }

  return response.json();
}

export async function fetchSkillSessionEvents(sessionId) {
  const response = await fetch(`${url}/record/skillSessionEvents/${encodeURIComponent(sessionId)}`, {
    method: 'GET',
    mode: 'cors',
    credentials: 'include',
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.msg || `fetchSkillSessionEvents failed: ${response.status}`);
  }

  return response.json();
}

export function recordSkillSessionEvent(payload) {
  if (getGuestModeActive()) {
    return Promise.resolve({ skipped: true, guest: true });
  }
  return fetch(`${url}/record/skillSessionEvent`, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
    .then((response) => {
      if (!response.ok) {
        return response.json().then((body) => {
          throw new Error(body.msg || `skillSessionEvent failed: ${response.status}`);
        });
      }
      return response.json();
    })
    .catch((error) => {
      console.warn('skillSessionEvent failed (non-blocking):', error.message);
      return { success: false };
    });
}

// ─── Paused Skill Sessions ────────────────────────────────────────────────────

export async function savePausedSkillSession(skillId, sessionData) {
    if (getGuestModeActive()) {
        return { success: true, skipped: true };
    }
    const response = await fetch(`${url}/record/savePausedSkillSession`, {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ skillId, ...sessionData }),
    });
    if (!response.ok) {
        throw new Error(`savePausedSkillSession failed: ${response.status}`);
    }
    return response.json();
}

export async function getPausedSkillSession(skillId) {
    if (getGuestModeActive()) {
        return null;
    }
    const response = await fetch(`${url}/record/getPausedSkillSession/${skillId}`, {
        method: 'GET',
        mode: 'cors',
        credentials: 'include',
    });
    if (!response.ok) {
        throw new Error(`getPausedSkillSession failed: ${response.status}`);
    }
    const data = await response.json();
    return data.session;
}

export async function deletePausedSkillSession(skillId) {
    if (getGuestModeActive()) {
        return { success: true, skipped: true };
    }
    const response = await fetch(`${url}/record/deletePausedSkillSession/${skillId}`, {
        method: 'DELETE',
        mode: 'cors',
        credentials: 'include',
    });
    if (!response.ok) {
        throw new Error(`deletePausedSkillSession failed: ${response.status}`);
    }
    return response.json();
}
